// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updatePdfSettings from "@bg/App/Handlers/PdfGenerator/UpdatePdfSettings";
import { CustomQuoteModel, getQuoteDetails as getQuote, GetQuoteDetails } from "@bg/App/Services/CustomQuoteService";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";

interface Props {
    apiParameter: GetQuoteDetails;
    apiResult: { successful: boolean; error?: any; quote?: CustomQuoteModel };
    realTimeInfo: { productsInfo: ProductInfo[]; realTimePricingFailed: boolean; realTimeInventoryFailed: boolean };
    specProductIds: string[];
}

type Parameter = GetQuoteDetails & {
    skipPricingAndInventory?: boolean;
    onSuccess?: () => void;
    onComplete?: (result: any) => void;
    onError?: (e: any) => void;
};

type HandlerType = Handler<Parameter, Props>;

export const BeginLoadingquotes: HandlerType = props => {
    props.dispatch({
        type: "Pages/MyCustomQuotes/BeginLoadingQuoteDetails",
    } as any);
};

export const DispatchBeginAction: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
    };
};

export const CallApiWithParameters: HandlerType = async props => {
    props.apiResult = await getQuote(props.apiParameter);
};

export const OnError: HandlerType = props => {
    if (!props.apiResult.successful) {
        props.dispatch({
            type: "Pages/MyCustomQuotes/CompleteLoadingQuoteDetails",
            errorLoading: true,
        } as any);
        props.parameter.onError?.(props.apiResult.error);
    }
};

export const CallOnComplete: HandlerType = props => {
    if (props.apiResult.successful && props.apiResult.quote) {
        if (props.parameter.skipPricingAndInventory) {
            props.dispatch({
                type: "Pages/MyCustomQuotes/CompleteLoadingQuoteDetails",
                quote: props.apiResult.quote,
                errorLoading: false,
            } as any);
            props.parameter.onComplete?.(props.apiResult);
            const quote = props.apiResult.quote;
            props.dispatch(
                updatePdfSettings({
                    parameters: {
                        ThirdPartyQuoteClientEmail: quote.email,
                        ThirdPartyQuoteWebsite: quote.website,
                        ThirdPartyQuoteClientName: quote.clientName,
                        ThirdPartyQuoteClientPhoneNumber: quote.phoneNumber,
                        ThirdPartyQuoteClientStreetAddress1: quote.streetAddress1,
                        ThirdPartyQuoteClientStreetAddress2: quote.streetAddress2,
                        ThirdPartyQuoteClientCity: quote.city,
                        ThirdPartyQuoteClientProvince: quote.province,
                        ThirdPartyQuoteClientPostalCode: quote.postalCode,
                        ThirdPartyQuoteFreightCharges: quote.freightCharges,
                        ThirdPartyQuoteLabourCharges: quote.labourCharges,
                        ThirdPartyQuoteTaxDisplayOption: quote.taxDisplayType,
                        ThirdPartyQuotePriceDisplayOption: quote.priceDisplayType,
                        ThirdPartyQuoteExpirationDate: quote.expiryDate,
                        pdfType: "ThirdPartyQuote",
                        projectName: quote?.name,
                        id: quote?.id,
                    },
                }),
            );
        }
    }
};

export const LoadRealTimePricing: HandlerType = async props => {
    props.realTimeInfo = { productsInfo: [], realTimePricingFailed: false, realTimeInventoryFailed: false };
    const alreadyExistProductInfo = (props.getState() as any).myCustomQuoteDetails.productsInfo;
    const { quote } = props.apiResult;
    const groups = quote?.groups.split(",");
    const productInfos = [] as ProductInfo[];
    props.specProductIds = [];

    if (!quote?.linesCount) {
        return;
    }
    /**
     * if the line info already exists dont pass it in the real time call again
     * in case the lineIfon exists but it failed to load invenyory or pricing, pass it in one more time
     */
    groups?.map(group => {
        if (quote?.groupLinesModel[group]) {
            quote.groupLinesModel[group].forEach(line => {
                const lineExists = Object.keys(alreadyExistProductInfo)?.filter(id => id === line.lineId)?.[0];
                if (
                    !lineExists ||
                    alreadyExistProductInfo[lineExists].failedToLoadInventory ||
                    alreadyExistProductInfo[lineExists].failedToLoadPricing
                ) {
                    productInfos.push({
                        ...createFromProduct(line.productResult),
                        qtyOrdered: line.qty,
                        lineId: line.lineId,
                    } as unknown as ProductInfo);
                    if(line.isSpecItem) {
                        props.specProductIds.push(line.lineId);
                    }
                }
                if (line.relatedItems && line.relatedItems.length > 0) {
                    line.relatedItems.forEach(related => {
                        const relatedExists = Object.keys(alreadyExistProductInfo)?.filter(
                            id => id === related.lineId,
                        )?.[0];
                        if (
                            !relatedExists ||
                            alreadyExistProductInfo[relatedExists].failedToLoadInventory ||
                            alreadyExistProductInfo[relatedExists].failedToLoadPricing
                        ) {
                            productInfos.push({
                                ...createFromProduct(related.productResult),
                                qtyOrdered: related.qty,
                                lineId: related.lineId,
                            } as unknown as ProductInfo);
                            if(related.isSpecItem) {
                                props.specProductIds.push(related.lineId);
                            }
                        }
                    });
                }
            });
        }
    });

    props.realTimeInfo.productsInfo = [...productInfos];
    if (!props.apiResult.successful || props.parameter.skipPricingAndInventory) {
        return;
    }
    let pricingLoaded = false;
    const productPriceParameters = productInfos.filter(p => !props.specProductIds.includes((p as any).lineId));
    if(!productPriceParameters.length) {
        return;
    }
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters,
            onSuccess: realTimePricing => {
                props.realTimeInfo.productsInfo = props.realTimeInfo.productsInfo.map(info => {
                    info.pricing = realTimePricing.realTimePricingResults?.find(o => o.productId === info.productId);
                    return info;
                });
                pricingLoaded = true;
            },
            onError: () => {
                props.realTimeInfo.productsInfo.forEach(info => {
                    info.failedToLoadPricing = true;
                });
                props.realTimeInfo.realTimePricingFailed = true;
                pricingLoaded = true;
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );

    await waitFor(() => pricingLoaded);
};

export const LoadRealTimeInventory: HandlerType = async props => {
    if (!props.apiResult.successful || props.parameter.skipPricingAndInventory) {
        return;
    }
    const { quote } = props.apiResult;
    if (!quote?.linesCount) {
        return;
    }
    const productIds = [] as string[];
    props.realTimeInfo.productsInfo.forEach(info => {
        if(!props.specProductIds.includes((info as any).lineId)) {
            productIds.push(info.productId);
        }
    });
    if (!productIds.length) {
        return;
    }

    let loadedInventory = false;

    props.dispatch(
        loadRealTimeInventory({
            productIds,
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    props.realTimeInfo.productsInfo.forEach(info => {
                        info.failedToLoadInventory = true;
                    });
                    props.realTimeInfo.realTimeInventoryFailed = true;
                }
                if (props.realTimeInfo.productsInfo) {
                    props.realTimeInfo.productsInfo = props.realTimeInfo.productsInfo.map(info => {
                        info.inventory = realTimeInventoryProps.apiResult?.realTimeInventoryResults?.find(
                            o => o.productId === info.productId,
                        );
                        return info;
                    });
                }
                loadedInventory = true;
            },
        }),
    );

    await waitFor(() => loadedInventory);
};

export const SetProductInfos: HandlerType = props => {
    if (!props.realTimeInfo?.productsInfo || !props.apiResult.successful) {
        return;
    }

    props.dispatch({
        type: "Pages/MyCustomQuotes/SetProductsInfo",
        realTimeInfo: props.realTimeInfo,
        quote: props.apiResult.quote,
    } as any);
};

export const DipatchLoadingProductsCompleted: HandlerType = props => {
    if (!props.apiResult.successful || props.parameter.skipPricingAndInventory) {
        return;
    }
    props.dispatch({
        type: "Pages/MyCustomQuotes/CompleteLoadingQuoteDetails",
        quote: props.apiResult.quote,
        errorLoading: false,
    } as any);
    props.parameter.onComplete?.(props.apiResult);
    const quote = props.apiResult.quote!;
    props.dispatch(
        updatePdfSettings({
            parameters: {
                ThirdPartyQuoteClientEmail: quote.email,
                ThirdPartyQuoteWebsite: quote.website,
                ThirdPartyQuoteClientName: quote.clientName,
                ThirdPartyQuoteClientPhoneNumber: quote.phoneNumber,
                ThirdPartyQuoteClientStreetAddress1: quote.streetAddress1,
                ThirdPartyQuoteClientStreetAddress2: quote.streetAddress2,
                ThirdPartyQuoteClientCity: quote.city,
                ThirdPartyQuoteClientProvince: quote.province,
                ThirdPartyQuoteClientPostalCode: quote.postalCode,
                ThirdPartyQuoteFreightCharges: quote.freightCharges,
                ThirdPartyQuoteLabourCharges: quote.labourCharges,
                ThirdPartyQuoteTaxDisplayOption: quote.taxDisplayType,
                ThirdPartyQuotePriceDisplayOption: quote.priceDisplayType,
                ThirdPartyQuoteExpirationDate: quote.expiryDate,
                pdfType: "ThirdPartyQuote",
                projectName: quote?.name,
                id: quote?.id,
            },
        }),
    );
};

export const chain = [
    BeginLoadingquotes,
    DispatchBeginAction,
    CallApiWithParameters,
    OnError,
    CallOnComplete,
    LoadRealTimePricing,
    LoadRealTimeInventory,
    SetProductInfos,
    DipatchLoadingProductsCompleted,
];

const getQuoteDetails = createHandlerChainRunner(chain, "GetQuoteDetails");
export default getQuoteDetails;
